import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, Typography, Divider } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import LanguageIcon from "@mui/icons-material/Language";
import PublicIcon from "@mui/icons-material/Public";
import TuneIcon from "@mui/icons-material/Tune";
import Langs from "../../../app/lang/langs";
import { checkPermission } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config.js";
import { SelectInput } from "../../../components/inputs/Select";
import { NumberInput } from "../../../components/inputs/NumberInput";
import {
  getSettings,
  setLocalSettings,
  updateSettings,
  setSelectedCountry,
  setLanguage,
} from "../settingsSlice";

const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const cardSx = {
  bgcolor: "#fff",
  borderRadius: "14px",
  boxShadow:
    "0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)",
  overflow: "hidden",
  transition: TRANSITION_SMOOTH,
};

const sectionHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 2,
  pt: 2,
  pb: 1.5,
};

const sectionTitleSx = {
  fontWeight: 700,
  fontSize: "0.95rem",
  color: "#1a1a1a",
  letterSpacing: "-0.01em",
};

export default function MobileGeneralSettingsTab() {
  const dispatch = useDispatch();

  const settings = useSelector((state) => state.settings);
  const localSettingsList = useSelector(
    (state) => state.settings.localSettingsList
  );

  const [lang, setLang] = useState(settings.lang);

  const languages = [
    { value: "en", label: "English" },
    { value: "fr", label: "Français" },
  ];

  const handleLanguageChange = (selectedOption) => {
    setLang(selectedOption.value);
    dispatch(setLanguage(selectedOption.value));
  };

  const handleParameterChange = (event) => {
    let changedParam = {
      value: event.target.value,
      id: event.target.id,
    };
    dispatch(setLocalSettings(changedParam));
  };

  function handleSaveLocalSettings() {
    dispatch(
      updateSettings({
        fields: Object.values(localSettingsList).map((s) => {
          let id = s.value;
          let value = s.settingValue;
          switch (s.type) {
            case "string":
              value = String(s.settingValue);
              break;
            case "number":
              value = Number(s.settingValue);
              break;
            default:
              break;
          }
          return { id: id, value: value };
        }),
        id_country:
          settings.selectedCountry && settings.selectedCountry.value,
      })
    ).then(() => dispatch(getSettings()));
  }

  const handleCountryChange = (option) => {
    dispatch(setSelectedCountry(option));
  };

  let isEdit = checkPermission(
    config.scopes.settings,
    config.permissionTypes.edit
  );

  const hasSettings = Object.values(localSettingsList).length > 0;

  return (
    <Box
      sx={{
        mt: 0,
        px: 0.5,
        pt: 1.5,
        pb: hasSettings && isEdit ? "calc(env(safe-area-inset-bottom, 0px) + 90px)" : 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* User settings card */}
      <Box sx={{ ...cardSx, overflow: "visible" }}>
        <Box sx={sectionHeaderSx}>
          <LanguageIcon sx={{ fontSize: 20, color: "#FF9015" }} />
          <Typography sx={sectionTitleSx}>
            <Langs str="User settings" />
          </Typography>
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              p: 1.5,
              bgcolor: "rgba(0,0,0,0.02)",
              borderRadius: "10px",
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "#444",
                flexShrink: 0,
              }}
            >
              <Langs str="LANGUAGE" />
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: 180 }}>
              <SelectInput
                key={"lang_selector"}
                value={lang}
                onChange={handleLanguageChange}
                options={languages}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Global settings card */}
      {hasSettings && isEdit && (
        <Box sx={cardSx}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              px: 2,
              pt: 2,
              pb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TuneIcon sx={{ fontSize: 20, color: "#FF9015" }} />
              <Typography sx={sectionTitleSx}>
                <Langs str="Global settings" />
              </Typography>
            </Box>

            {/* Country selector */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <PublicIcon sx={{ fontSize: 16, color: "#888" }} />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.8rem",
                  color: "#555",
                  whiteSpace: "nowrap",
                }}
              >
                <Langs str="country" />
              </Typography>
              <Box sx={{ minWidth: 120 }}>
                <SelectInput
                  value={
                    settings.selectedCountry &&
                    settings.selectedCountry.value
                  }
                  onChange={handleCountryChange}
                  options={[
                    { value: null, label: "Default" },
                    ...settings.countries,
                  ]}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mx: 2, borderColor: "rgba(0,0,0,0.05)" }} />

          {/* Settings list */}
          <Box sx={{ px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
            {Object.values(localSettingsList).map((setting, idx) => (
              <Box
                key={`ITEM_${setting.id}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  py: 1,
                  px: 1.5,
                  borderRadius: "10px",
                  bgcolor: idx % 2 === 0 ? "rgba(0,0,0,0.015)" : "transparent",
                  transition: TRANSITION_SMOOTH,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.875rem",
                    color: "#333",
                    flex: 1,
                    minWidth: 0,
                    lineHeight: 1.4,
                  }}
                >
                  <Langs str={setting.label} />
                </Typography>
                <Box sx={{ width: 90, flexShrink: 0 }}>
                  <NumberInput
                    name={setting.label}
                    min={setting.min || 0}
                    id={setting.id}
                    data-key={setting.value}
                    max={setting.max || 999}
                    onChange={handleParameterChange}
                    value={setting.settingValue}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Fixed bottom save button */}
      {hasSettings && isEdit && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
            bgcolor: "rgba(255,255,255,0.78)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderTop: "1px solid rgba(255, 144, 21, 0.12)",
            zIndex: 10,
            boxShadow:
              "0 -4px 24px rgba(255, 144, 21, 0.06), 0 -1px 6px rgba(0, 0, 0, 0.03)",
          }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={handleSaveLocalSettings}
            startIcon={<SaveIcon />}
            sx={{
              background:
                "linear-gradient(135deg, #FF9015 0%, #FF8701 50%, #e07800 100%)",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              minHeight: 50,
              borderRadius: "14px",
              fontSize: "16px",
              boxShadow: "0 4px 16px rgba(255,144,21,0.3)",
              transition: TRANSITION_SMOOTH,
              "&:hover": {
                background:
                  "linear-gradient(135deg, #FF8701 0%, #e07800 100%)",
              },
              "&:active": { transform: "scale(0.98)" },
            }}
          >
            <Langs str="SAVE" />
          </Button>
        </Box>
      )}
    </Box>
  );
}
